<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<vl-interaction-select
  :ident="`select-target-${index}`"
  :condition="selectCondition"     :filter="filterFunction"
  :features.sync="selectedFeatures"
  :toggle-condition="never"
  :remove-condition="shiftKeyOnly"
  :multi="true"
  ref="interactionSelect"
>
  <vl-style-func :factory="styleFunctionFactory" />
</vl-interaction-select>
</template>

<script>
import {isCluster} from '@/utils/style-utils.js';
import { singleClick, never, shiftKeyOnly } from 'ol/events/condition';

import { Style, Circle, Fill, Stroke } from 'ol/style';
import MultiPoint from 'ol/geom/MultiPoint';

export default {
  name: 'select-interaction',
  props: {
    index: String
  },
  computed: {
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    imageWrapper() {
      return this.$store.getters['currentProject/currentViewer'].images[this.index];
    },
    selectedFeatures: {
      get() {
        return this.imageWrapper.selectedFeatures.selectedFeatures;
      },
      set(value) {
        //used when selecting a vertex of a feature
        let notAnnotations = value.filter(x => !Object.keys(x).includes('id') && x.properties === null);
        if(notAnnotations.length == 1){
          value = notAnnotations;
          this.$store.commit(this.imageModule + 'setSelectedFeatures', value);
          return;
        }

        value.sort(
          function( a, b ) {
            if( a.properties.annot.area > b.properties.annot.area ) return 1;
            else return -1;
          }
        );

        let previousTarget = this.imageWrapper.selectedFeatures.selectionTargetedFeatures;
        let previousSelectedFeature = this.imageWrapper.selectedFeatures.selectedFeatures[0];

        this.$store.commit(this.imageModule + 'setSelectionTargetedFeatures', value);

        //see https://github.com/cytomine/Cytomine-Web-UI/issues/13 for more details of this algorithm
        if(this.imageWrapper.selectedFeatures.selectedFeatures.length == 0){
          if(value.length>=1) value = [value[0]];
        }
        else {
          if(value.length > 1){
            //if previous selection is in the new target, we will take the first element not yet visited
            if(value.map(x => x.id).includes(previousSelectedFeature.id)) {
              var index = previousTarget.findIndex(x => x.id === previousSelectedFeature.id);
              let visitedFeatures = previousTarget.slice(0, index + 1);
              index = 0;
              for(var i = 0; i < value.length; i++){
                if(!visitedFeatures.map(x => x.id).includes(value[i].id)) {
                  index = i;
                  break;
                }
              }
              value = [value[index]];
            }
            else {
              value = [value[0]];
            }
          }
        }

        if (value.length >= 1 && value[0].properties !== null) {
          let annot = value[0].properties.annot;
          if (typeof annot.recordAction === 'function') {
            annot.recordAction();
          }
        }
        this.$store.commit(this.imageModule + 'setSelectedFeatures', value);
      }
    },
    terms() {
      return this.imageWrapper.style.terms || [];
    },
    styleFunctionFactory() {
      this.imageWrapper.selectedFeatures.selectedFeatures;
      this.imageWrapper.style.layersOpacity;
      this.terms.forEach(term => {
        term.visible;
        term.color;
        term.opacity;
      });
      this.imageWrapper.style.displayNoTerm;
      this.imageWrapper.style.noTermOpacity;
      this.imageWrapper.draw.activeEditTool; // style is different in edit mode (vertices displayed)
      this.imageWrapper.properties.selectedPropertyValues;
      this.imageWrapper.properties.selectedPropertyColor;
      this.imageWrapper.review.reviewMode;
      if (this.imageWrapper.style.wrappedTracks) {
        this.imageWrapper.style.wrappedTracks.forEach(track => {
          track.color;
        });
      }

      let activeEditTool = this.imageWrapper.draw.activeEditTool;

      return (feature, resolution) => {
        // 기존 Vuex의 기본 스타일 함수 호출
        let baseStyleFn = this.$store.getters[this.imageModule + 'genStyleFunction'];
        let styles = baseStyleFn(feature, resolution);
        
        // 배열이 아닐 경우 배열로 변환
        if (styles && !Array.isArray(styles)) {
          styles = [styles];
        }
        else if (!styles) {
          styles = [];
        }

        // [수정 2] Modify 툴이 선택되었을 때 도형의 좌표를 추출하여 정점 렌더링
        if (activeEditTool === 'modify' && feature.getGeometry()) {
          const geometry = feature.getGeometry();
          const geomType = geometry.getType();
          let coordinates = [];

          if (geomType === 'Polygon') {
            coordinates = geometry.getCoordinates()[0]; // 다각형 외곽선
          }
          else if (geomType === 'LineString') {
            coordinates = geometry.getCoordinates(); // 선
          }
          else if (geomType === 'MultiLineString') {
            coordinates = geometry.getCoordinates().flat(1); // 다중 선
          }

          if (coordinates.length > 0) {
            styles.push(new Style({
              geometry: new MultiPoint(coordinates),
              image: new Circle({
                radius: 4, // 정점 크기
                fill: new Fill({ color: '#ff4757' }), // 눈에 띄는 빨간색
                stroke: new Stroke({ color: '#ffffff', width: 2 })
              }),
              zIndex: Infinity // 최상단에 표시
            }));
          }
        }
        return styles;
      };
    },
    filterFunction() {
      return feature => !isCluster(feature);
    },
    never() {
      return never;
    },
    shiftKeyOnly() {
      return shiftKeyOnly;
    },
    selectCondition() {
      return (mapBrowserEvent) => {
        if (!singleClick(mapBrowserEvent)) {
          return false;
        }

        const activeEditTool = this.imageWrapper.draw.activeEditTool;
        const ongoingEdit = this.imageWrapper.draw.ongoingEdit;

        // edit 중이 아니면 기존처럼 동작
        if (!activeEditTool) {
          return true;
        }

        // 실제 drag/modify sequence 중에는 deselect 금지
        if (ongoingEdit) {
          return false;
        }

        const map = mapBrowserEvent.map;
        const pixel = mapBrowserEvent.pixel;

        // 현재 선택된 feature의 id 목록
        const selectedIds = new Set(
          this.imageWrapper.selectedFeatures.selectedFeatures.map(f => f.id)
        );

        let hitUnselected = false;
        let hitSelected = false;

        map.forEachFeatureAtPixel(pixel, (feature) => {
          const annotId = feature.get('annot')?.id;
          if (annotId == null) return; // vertex feature 등 무시
          if (selectedIds.has(annotId)) {
            hitSelected = true;
          }
          else {
            hitUnselected = true;
          }
        });

        console.log('[SelectCondition] hitUnselected:', hitUnselected, '| hitSelected:', hitSelected);

        // 선택되지 않은 annotation을 클릭한 경우 → 선택 허용
        if (hitUnselected) return true;

        // 빈 영역 클릭 → 선택 해제 허용
        if (!hitSelected && !hitUnselected) return true;

        // 이미 선택된 annotation만 클릭한 경우 → modify가 처리하게 select는 막음
        return false;
      };
    },
  },
  watch: {
    async styleFunctionFactory() {
      // HACK: style function is not called again when redefined => force the update of style for selected features
      if(this.$refs.interactionSelect && this.$refs.interactionSelect.$interaction) {
        await this.$refs.interactionSelect.$interaction.getFeatures().forEach(ft => ft.changed());
      }
    }
  }
};
</script>
